interface PopoverObserver {
    disconnect(): void;
}

// Use a WeakMap to store observers for each popUp element
const popoverObserversMap = new WeakMap<HTMLElement, PopoverObserver[]>();

(window as any).popUpHelper = {
    /**
     * Positions and shows a popover element relative to a container element.
     * @param popUp The popover HTMLElement to show and position.
     * @param container The container HTMLElement to position relative to.
     * @param matchWidth should the popover match the size of the container.
     * @param scrollable is the popover scrollable?
     * @param closeOnScroll should the popup be closed on scroll. True will close, false will update position
     * @param alignment Alignment string: 'TopLeft', 'TopCenter', 'TopRight', 'BottomLeft', 'BottomCenter', 'BottomRight'
     */
    positionPopUp: function (popUp: HTMLElement, container: HTMLElement, matchWidth: boolean = false, scrollable: boolean = false, closeOnScroll:boolean = true, dotnetRef:any, alignment: string = "TopRight") {
        if (!popUp || !container) return;

        function clamp(number: number, min: number, max: number) {
            return Math.max(min, Math.min(number, max));
        }

        function calculatePopUpPosition() {
            // this gets coordinates relative to the viewport, not the document, so it's perfect for our needs since
            // the pop-up container is fixed to the viewport and never scrolls with the document
            const rect = container.getBoundingClientRect();

            if (matchWidth) {
                popUp.style.width = `${rect.width}px`;
            }

            const popupUpwards = alignment.includes("Top");
            const popupDownwards = alignment.includes("Bottom");
            const popupLeftwards = alignment.includes("Left");
            const popupRightwards = alignment.includes("Right");
            const popupCenter = alignment.includes("Center");
            const popWidth = popUp.querySelector("div").scrollWidth;
            const popHeight = popUp.querySelector("div").scrollHeight;

            // because our PopupContainer uses a fixed overlay that is pinned to the available viewport area, we're 
            // going to use viewport-relative coordinates for all calculations (i.e. not including scrollX or scrollY)
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            // calculate left based on horizontal component of alignment
            let left = 0;
            if (popupLeftwards) {
                left = rect.left;
            } else if (popupRightwards) {
                left = rect.right - popWidth;
            } else if (popupCenter) {
                left = rect.left + (rect.width / 2) - (popWidth / 2);
            }

            // calculate top based on vertical component of alignment 
            let top = 0;
            if (popupUpwards) {
                top = rect.top - popHeight;
            } else if (popupDownwards) {
                top = rect.bottom;
            }

            // Clamp the popups height and top position.
            const style = window.getComputedStyle(popUp);
            if (scrollable && top + popHeight > viewportHeight) {
                const minHeightValue = style.getPropertyValue("min-height");
                let minHeight = 0;
                if (minHeightValue) {
                    minHeight = parseFloat(minHeightValue);
                }
                popUp.style.bottom = '0px'; // force the height.
                if (minHeight > 0 && top + minHeight > viewportHeight) {
                    top = clamp(top, 0, viewportHeight - minHeight);
                }
            } else {
                top = clamp(top, 0, viewportHeight - popHeight);
            }

            // Clamp the popups width and left position.
            if (scrollable && top + popWidth > viewportWidth) {
                const minWidthValue = style.getPropertyValue("min-width");
                let minWidth = 0;
                if (minWidthValue) {
                    minWidth = parseFloat(minWidthValue);
                }
                popUp.style.right = '0px'; // force the width.
                if (minWidth > 0 && left + minWidth > viewportWidth) {
                    left = clamp(left, 0, viewportWidth - minWidth);
                }
            } else {
                left = clamp(left, 0, viewportWidth - popWidth);
            }

            return {top: Math.round(top), left: Math.round(left)};
        }
        
        
        function updatePosition() {
            const position = calculatePopUpPosition();
            // set styles
            popUp.style.left = `${position.left}px`;
            popUp.style.top = `${position.top}px`;
        }

        // Initial position
        updatePosition();

        // Clean up any previous observers
        const prevObservers = popoverObserversMap.get(popUp);
        if (prevObservers && Array.isArray(prevObservers)) {
            prevObservers.forEach(obs => obs.disconnect && obs.disconnect());
        }
        const observers: PopoverObserver[] = [];

        // Observe container resize/move
        if (window.ResizeObserver) {
            const resizeObs = new ResizeObserver(updatePosition);
            resizeObs.observe(container);
            observers.push(resizeObs as PopoverObserver);
        }

        // Observe DOM mutations that might move the container
        const mutationObs = new MutationObserver(updatePosition);
        mutationObs.observe(document.body, {attributes: true, childList: true, subtree: true});
        observers.push(mutationObs as PopoverObserver);
        function onScrollOrResize() {
            if (closeOnScroll) {
                // We detect if the position would change from the scroll event.
                // If so, we pass the event back to blazor to close the popup.
                const position = calculatePopUpPosition();
                if (parseInt(popUp.style.left) != position.left || parseInt(popUp.style.top) != position.top) {
                    dotnetRef.invokeMethodAsync("HandleScroll");
                }
            } else {
                updatePosition();
            }
        }

        window.addEventListener('scroll', onScrollOrResize, true);
        window.addEventListener('resize', onScrollOrResize, true);

        observers.push({
            disconnect: () => {
                window.removeEventListener('scroll', onScrollOrResize, true);
                window.removeEventListener('resize', onScrollOrResize, true);
            }
        });

        popoverObserversMap.set(popUp, observers);
    },

    /**
     * Disposes the popover by cleaning up observers and listeners.
     * @param popUp The popover HTMLElement to dispose.
     */
    disposePopUp: function (popUp: HTMLElement) {
        const observers = popoverObserversMap.get(popUp);
        if (observers && Array.isArray(observers)) {
            observers.forEach(obs => obs.disconnect && obs.disconnect());
        }
        popoverObserversMap.delete(popUp);
    }
};